import { getCollaborationDisplayStatus } from './collaborationDisplayStatus';
import {
  countActiveJourneyItems,
  isJourneyItemComplete,
} from './collaborationJourneyStats';
import {
  enrichOperationsItem,
  filterOperationsList,
  partitionOperationsItems,
  sortOperationsList,
} from './OperationsListUtils';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function getInfluencerAvatarUrl(item) {
  return item?.avatar_url || item?.profile_picture_url || null;
}

export const WORKFLOW_STAGES = {
  CONTRACT: 'Contract Signing',
  SHIPMENT: 'Product Shipment',
  CONTENT_CREATION: 'Content Creation',
  CONTENT_REVIEW: 'Content Review',
  PAYMENT: 'Payment Processing',
  COMPLETE: 'Completed',
  ONBOARDING: 'Onboarding',
};

export const HEALTH_LABELS = {
  healthy: 'Healthy',
  needs_attention: 'Needs Attention',
  at_risk: 'At Risk',
};

const STAGE_KEYWORDS = [
  { stage: WORKFLOW_STAGES.PAYMENT, match: (label) => /payment/i.test(label) },
  { stage: WORKFLOW_STAGES.CONTRACT, match: (label) => /contract|signature|approve contract/i.test(label) },
  { stage: WORKFLOW_STAGES.SHIPMENT, match: (label) => /ship|address|product shipped/i.test(label) },
  { stage: WORKFLOW_STAGES.CONTENT_REVIEW, match: (label) => /review deliverable|review content|waiting for review/i.test(label) },
  { stage: WORKFLOW_STAGES.CONTENT_CREATION, match: (label) => /content|access|grant|confirmation|waiting for content/i.test(label) },
];

const PENDING_ONBOARDING_LABELS = new Set([
  'Application pending',
  'Respond to invite',
  'Review application',
  'Invite pending',
]);

export function deriveWorkflowStage(journeyLabel) {
  const label = String(journeyLabel || '').trim();
  if (!label || label === 'Complete') return WORKFLOW_STAGES.COMPLETE;
  if (label === 'Getting started' || PENDING_ONBOARDING_LABELS.has(label)) {
    return WORKFLOW_STAGES.ONBOARDING;
  }

  const match = STAGE_KEYWORDS.find(({ match: fn }) => fn(label));
  return match?.stage || WORKFLOW_STAGES.CONTENT_CREATION;
}

function pickActivityTimestamp(item, stepSource) {
  const candidates = [
    stepSource?.updated_at,
    stepSource?.updatedAt,
    stepSource?.last_activity_at,
    item?.updated_at,
    item?.response_at,
    item?.created_at,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (value) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) return date;
    }
  }
  return new Date(item?.created_at || Date.now());
}

export function getDaysWaiting(item, stepSource) {
  if (isJourneyItemComplete(item)) return 0;
  const activityDate = pickActivityTimestamp(item, stepSource);
  const diff = Date.now() - activityDate.getTime();
  return Math.max(0, Math.floor(diff / MS_PER_DAY));
}

export function deriveCollaborationHealth(item, daysWaiting) {
  if (isJourneyItemComplete(item)) return 'healthy';

  const waiting = Boolean(item?.journey_waiting);
  const brandOwned = !waiting && !isJourneyItemComplete(item);

  if (daysWaiting >= 7) return 'at_risk';
  if (brandOwned && daysWaiting >= 3) return 'at_risk';
  if (daysWaiting >= 4 || (brandOwned && daysWaiting >= 2)) return 'needs_attention';
  return 'healthy';
}

function pickValidCampaignImage(images) {
  if (!Array.isArray(images)) return null;
  return images.find((img) => img && !String(img).startsWith('blob:')) || null;
}

function extractCampaignThumbnailFromSource(source) {
  if (!source) return null;

  const fromArray = pickValidCampaignImage(source.campaign_images);
  if (fromArray) return fromArray;

  if (typeof source.campaign_images === 'string' && source.campaign_images.trim()) {
    try {
      const parsed = JSON.parse(source.campaign_images);
      const fromJson = pickValidCampaignImage(parsed);
      if (fromJson) return fromJson;
    } catch (error) {
      // ignore invalid JSON payloads
    }
  }

  const url = source.campaign_image_url || source.image_url;
  if (url && !String(url).startsWith('blob:')) return url;
  return null;
}

export function getCampaignThumbnailUrl(...sources) {
  for (let i = 0; i < sources.length; i += 1) {
    const thumbnail = extractCampaignThumbnailFromSource(sources[i]);
    if (thumbnail) return thumbnail;
  }
  return null;
}

export function enrichBrandOperationsItem(journeyItem, stepSource) {
  return enrichOperationsItem(journeyItem, stepSource, {
    deriveHealth: deriveCollaborationHealth,
    getDaysWaiting,
    deriveWorkflowStage,
    getCampaignThumbnailUrl,
    getPartyAvatar: (step, item) => getInfluencerAvatarUrl(step) || getInfluencerAvatarUrl(item),
    partyAvatarKey: 'creator_avatar',
    payoutPerspective: 'brand',
  });
}

export function buildBrandOperationsStats(items) {
  const active = items.filter((item) => !isJourneyItemComplete(item));
  return {
    needsAction: items.filter((item) => !item.journey_waiting && !isJourneyItemComplete(item)).length,
    waitingOnCreator: items.filter((item) => item.journey_waiting && !isJourneyItemComplete(item)).length,
    completed: items.filter((item) => isJourneyItemComplete(item)).length,
    atRisk: active.filter((item) => item.health === 'at_risk').length,
  };
}

/** Matches Collaborations page Active summary card count. */
export function countActiveBrandJourneyItems(journeyItems, collaborationStatuses = {}) {
  return countActiveJourneyItems(journeyItems, (item) => {
    const collab = collaborationStatuses[item.id];
    return getCollaborationDisplayStatus(
      item.status,
      collab?.acceptanceStatus || collab?.status,
    ) === 'completed';
  });
}

export function partitionBrandOperationsItems(items) {
  return partitionOperationsItems(items);
}

export function filterOperationsItems(items, options = {}) {
  return filterOperationsList(items, {
    ...options,
    partyFields: ['influencer_name'],
  });
}

export function sortOperationsItems(items, sortBy = 'urgency') {
  return sortOperationsList(items, sortBy, {
    partySortKey: 'creator',
    partyFields: ['influencer_name'],
  });
}
